'use client'

import {
    Alert as BaseComponent,
    type AlertProps as BaseProps,
    useStyleConfig,
} from '@chakra-ui/react'
import { noop } from 'lodash'
import React, { type ReactNode } from 'react'
import {
    AttentionIcon,
    CloseIcon,
    ErrorIcon,
    InfoIcon,
    SuccessIcon,
} from '../../icons'
import { Divider } from '../Divider'
import { IconButton } from '../IconButton'
import { Stack } from '../Layout'
import { Box } from '../Layout/Box/Box'
import { Flex } from '../Layout/Flex/Flex'
import { Text } from '../Typography/Text'

type Omitted = 'status' | 'size' | 'colorScheme' | 'variant'

export type AlertProps = Omit<BaseProps, Omitted> & {
    /**
     * @default "default"
     */
    variant?: 'default' | 'info' | 'success' | 'error' | 'attention'
    /**
     * Renders a node after **"children"** and before **"icon"**(if it exists).
     */
    CTA?: ReactNode
    /**
     * @default false
     */
    isClosable?: boolean
    onClose?: () => void
}

const variantIcons = {
    default: <InfoIcon color="neutral.1400" />,
    info: <InfoIcon color="primary.main" />,
    success: <SuccessIcon />,
    error: <ErrorIcon />,
    attention: <AttentionIcon />,
}

export const Alert = ({
    variant,
    children,
    CTA = null,
    isClosable = false,
    onClose = noop,
    ...props
}: AlertProps) => {
    const styles = useStyleConfig('Alert', {
        variant,
        CTA,
        isClosable,
        ...props,
    })
    return (
        <BaseComponent sx={styles} {...props}>
            <Flex
                justify="flex-end"
                align="center"
                w="full"
                gap={CTA ? 1 : 2}
                my={[4, 2]}
            >
                <Flex gap={2} flexGrow={1} align="center">
                    <Box>{variantIcons[variant || 'default']}</Box>
                    <Text color="neutral.1900" w="full">
                        {children}
                    </Text>
                </Flex>
                <Stack spacing={1} direction="row" alignItems="center">
                    {CTA ? (
                        <>
                            <Divider h={6} w="1px" ms={2} />
                            {CTA}
                        </>
                    ) : null}

                    {isClosable ? (
                        <>
                            {CTA ? <Divider h={6} w="1px" me={1} /> : null}
                            <IconButton
                                tooltipProps={{ label: '' }}
                                data-testid="closeToast"
                                variant="ghost"
                                ms="auto"
                                onClick={onClose}
                            >
                                <CloseIcon size="md" />
                            </IconButton>
                        </>
                    ) : null}
                </Stack>
            </Flex>
        </BaseComponent>
    )
}
